import React from 'react';
import { get, uniqBy } from 'lodash';

import { APP_CATEGORIES, APP_TYPES } from 'constants/app';

import { formatCurrencyMaybeRound, hasPermission } from 'utils';
import { wordify } from 'shared/utils';
import { APP_SCREENSHOTS_SHOWN, getAppScreenshots } from 'shared/app-images';
import {
  isVersionGreater,
  getNewPublishableVersionRecord,
  getNewPublishableVersion,
  getOpenReviewVersion,
} from 'shared/app-versions';

import Status from 'components/status';
import Tooltip from 'components/tooltip';

const { CLIENT_URL, PUBLIC_SITE_URL } = process.env;

const ENTITY_DELIMITERS = Object.freeze(['/', '.']);

// App display id length before it is truncated
export const MAX_APP_ID_DISPLAY_LEN = 18;

export {
  APP_SCREENSHOTS_SHOWN,
  getAppScreenshots,
  isVersionGreater,
  getNewPublishableVersionRecord,
  getNewPublishableVersion,
  getOpenReviewVersion,
};

/**
 * Every `[id, label]` in a category tree, at any depth.
 * @param {Array<object>} categories
 * @returns {Array<[string, string]>}
 */
function categoryLabelEntries(categories) {
  return categories.flatMap((category) => [
    [category.id, category.label],
    ...categoryLabelEntries(category.categories || []),
  ]);
}

// An app stores only the leaf ids it was tagged with, so the tree is flattened
// once into the lookup those ids need
const CATEGORY_LABELS_BY_ID = new Map(categoryLabelEntries(APP_CATEGORIES));

/**
 * Labels for the categories an app is tagged with, in the order it lists them.
 * An id with no matching category is dropped rather than shown raw.
 * @param {object} app
 * @returns {Array<string>}
 */
export function getAppCategoryLabels(app) {
  return (app?.categories || [])
    .map((categoryId) => CATEGORY_LABELS_BY_ID.get(categoryId))
    .filter(Boolean);
}

/**
 * `APP_CATEGORIES` flattened into checkbox options — one per node at any depth,
 * in tree order. `depth` drives the visual indentation; a node that has
 * children is marked `heading: true` and rendered as a non-selectable label.
 * @returns {Array<{ id: string, label: string, depth: number, heading: boolean }>}
 */
export function getAppCategoryOptions() {
  const walk = (categories, depth) =>
    categories.flatMap((category) => {
      const children = category.categories || [];

      return [
        {
          id: category.id,
          label: category.label,
          depth,
          heading: children.length > 0,
        },
        ...walk(children, depth + 1),
      ];
    });

  return walk(APP_CATEGORIES, 0);
}

function findAppCategory(id, categories = APP_CATEGORIES) {
  for (const category of categories) {
    if (category.id === id) {
      return category;
    }

    const found = findAppCategory(id, category.categories || []);
    if (found) {
      return found;
    }
  }

  return null;
}

function appCategoryLineage(category) {
  return [
    category.id,
    ...(category.categories || []).flatMap(appCategoryLineage),
  ];
}

/**
 * Category ids with every descendant, so a group matches apps tagged with
 * its leaves as well as with the group itself.
 * @param {Array<string>} ids
 * @returns {Array<string>}
 */
export function expandAppCategoryIds(ids) {
  const expanded = ids.flatMap((id) => {
    const category = findAppCategory(id);
    return category ? appCategoryLineage(category) : [id];
  });

  return [...new Set(expanded)];
}

const FULL_ACCESS_LABEL =
  'Full access to your store, including products, orders, customers, and settings';

const PRICE_DESCRIPTIONS = Object.freeze({
  monthly: 'Charged monthly to your Swell account',
  once: 'Charged once to your Swell account',
});

// price_external excludes price and price_interval: the app is free to install
// and whatever its own service costs is settled with the provider
const EXTERNAL_PRICE_DESCRIPTION =
  'Requires a paid service from the app provider';

/**
 * Get app type label.
 * @param {string} id
 * @returns {string}
 */
export function getAppTypeLabel(id) {
  return APP_TYPES.find((type) => type.id === id)?.label || id;
}

/**
 * Render app type label.
 * @param {string} id
 * @param {boolean} renderTooltip
 * @returns {string}
 */
export function renderAppTypeLabel(id, renderTooltip = true) {
  const typeDef = APP_TYPES.find((type) => type.id === id);

  const labelElement = (
    <Status type="muted" dot={false}>
      {typeDef?.label || id}
    </Status>
  );

  return typeDef && renderTooltip ? (
    <Tooltip message={APP_TYPES.find((type) => type.id === id)?.description}>
      {labelElement}
    </Tooltip>
  ) : (
    labelElement
  );
}

/**
 * Get frontend URL for an installed app.
 * @param {object} installedApp
 * @returns {string}
 */
export function getFrontendURL(installedApp) {
  if (!installedApp) return '';

  const { parent_id, id } = installedApp;

  return CLIENT_URL.replace('CLIENT_ID', `${parent_id}--${id}--app`);
}

/**
 * Public marketplace listing filtered to a single app, so a release can be
 * previewed there before it exists. The public API serves the app's current
 * details for this id whether or not it's published.
 * @param {string} appId
 * @returns {string}
 */
export function getMarketplacePreviewURL(appId) {
  const base = PUBLIC_SITE_URL.replace(/\/$/, '');

  return `${base}/apps?preview=${encodeURIComponent(appId)}`;
}

/**
 * Public marketplace page of a published app.
 * @param {string} publicId
 * @returns {string}
 */
export function getAppPageURL(publicId) {
  const base = PUBLIC_SITE_URL.replace(/\/$/, '');

  return `${base}/apps/${encodeURIComponent(publicId)}`;
}

/**
 * Public marketplace listing, filtered by a category when given. The
 * marketplace redirects that to the category's own page when it has one.
 * @param {string} [category]
 * @returns {string}
 */
export function getMarketplaceURL(category) {
  const base = PUBLIC_SITE_URL.replace(/\/$/, '');

  return category ? `${base}/apps?categories=${category}` : `${base}/apps`;
}

/**
 * @param {object} nav
 * @param {object} installedApp
 * @returns {string}
 */
export function translateAppLink(nav, installedApp) {
  let { link } = nav;

  if (installedApp) {
    if (link.startsWith('frontend://')) {
      if (nav.target === 'blank') {
        link = link.replace('frontend://', getFrontendURL(installedApp) + '/');
      } else {
        link = link.replace(
          'frontend://',
          `/app/${getAppSlugId(installedApp.app)}/`,
        );
      }
    } else {
      if (!nav.target || nav.target === 'self') {
        link = `/app/${getAppSlugId(installedApp.app)}/`;
      }
    }
  }

  return link;
}

/**
 * Try to extract data from the dictionary first by the original key (`name`),
 * and then by the unique collection identifier.
 *
 * The collection name can be passed either with a unique identifier or with a public/private ID.
 *
 * For example
 * * unique collection identifiers
 * * * `apps/<objectid>/model`
 * * * `apps/<objectid>/model:child`
 * * * `products:apps.<objectid>.child`
 * * alternate collection identifiers
 * * * `apps/<app_name>/model`
 * * * `apps/<app_name>/model:child`
 * * * `products:apps.<app_name>.child`
 *
 * First, we try to extract data from the `dict` based on the passed `name`.
 * If this succeeds, then the value by key is simply returned.
 *
 * Otherwise, we try to convert the collection's alternative name to a unique id.
 *
 * If an alternative collection name was passed with `<app_name>`,
 * it will be converted to a unique identifier with `<objectid>`
 * and will be used as the `dict` key.
 *
 * @template T
 * @param {Record<string, T>} dict
 * @param {object} client
 * @param {string} name
 * @returns {T | undefined}
 */
export function extractEntityByCollectionName(dict, client, name) {
  let value = dict[name];

  if (value) {
    return value;
  }

  let char = '';
  let pos = Number.POSITIVE_INFINITY;

  for (const c of ENTITY_DELIMITERS) {
    const index = name.indexOf(c);

    if (index !== -1 && index < pos) {
      char = c;
      pos = index;
    }
  }

  if (Number.isFinite(pos)) {
    const posChild = name.indexOf(':');
    const namespace = name.slice(posChild < pos ? posChild + 1 : 0, pos);

    switch (namespace) {
      case 'apps': {
        pos += char.length;
        const appSlugId = name.slice(pos, name.indexOf(char, pos));
        const app = client.appsBySlug[appSlugId];

        if (app) {
          const altName = name.replace(
            `apps${char}${appSlugId}${char}`,
            `apps${char}${app.id}${char}`,
          );

          return dict[altName];
        }

        break;
      }

      default:
        break;
    }
  }

  return value;
}

/**
 * Get the app version a new review comment belongs to. A reply inherits the
 * version of the thread it answers, so a reviewer answering an earlier review
 * keeps that thread whole; a new thread takes the version under review.
 * @param {object} app
 * @param {string} [topId] id of the thread a reply answers
 * @param {string} [viewedVersion] version in view, used when no review is open
 * @returns {string|undefined}
 */
export function getReviewCommentVersion(app, topId, viewedVersion) {
  if (topId) {
    const thread = app.review_comments?.results?.find(({ id }) => id === topId);

    return thread?.version;
  }

  return getOpenReviewVersion(app) || viewedVersion;
}

/**
 * Fields required before an app can be submitted for review.
 * `skipForThemes` fields are not required for apps of type `theme`.
 */
export const APP_REVIEW_MANDATORY_FIELDS = Object.freeze([
  {
    field: 'name',
    label: 'Name',
    tip: 'Add a name for your app (name field in swell.json).',
  },
  {
    field: 'description',
    label: 'Description',
    tip: 'Add a short description for your app (description field in swell.json).',
  },
  {
    field: 'image_assets.0',
    label: 'Images',
    tip: 'Add at least one image (images field in swell.json).',
    skipForThemes: true,
  },
  {
    field: 'logo_icon.url',
    label: 'Logo icon',
    tip: 'Upload a logo icon (icon.* in assets folder).',
    skipForThemes: true,
  },
]);

/**
 * Get the list of mandatory app fields missing a value.
 * @param {object} app
 * @returns {Array<{ field: string, label: string, tip: string }>}
 */
export function getMissingReviewFields(app) {
  const isTheme = app?.type === 'theme';

  return APP_REVIEW_MANDATORY_FIELDS.filter(({ field, skipForThemes }) => {
    if (isTheme && skipForThemes) {
      return false;
    }

    return !get(app, field);
  });
}

/**
 * Check if app update is available.
 * @param {object} app
 * @param {object} installedApp
 * @returns {string|null}
 */
export function appUpdateAvailable(app, installedApp) {
  if (!installedApp) {
    return null;
  }

  const isPrivate = app.client_id === installedApp.parent_id;
  const availableVersion = isPrivate
    ? app.version
    : app.published_props?.version;

  return installedApp.version &&
    isVersionGreater(availableVersion, installedApp.version)
    ? availableVersion
    : null;
}

/**
 * Check if app is owned by the client.
 * @param {object} app
 * @param {object} client
 * @returns {boolean}
 */
export function isOwnApp(app, client) {
  return app.client_id === client.id;
}

/**
 * Removes the leading `_` Swell uses internally to mark a private
 * (unpublished) app ID — it's an internal mechanism and shouldn't be
 * shown to devs/users.
 * @param {string} id
 * @returns {string}
 */
export function stripPrivateIdPrefix(id) {
  return id && id.startsWith('_') ? id.slice(1) : id;
}

/**
 * @param {object} app
 * @returns {string}
 */
export function getAppSlugId(app) {
  return stripPrivateIdPrefix(app.private_id);
}

/**
 * The storefront app a theme runs on, out of the storefront shop list: the
 * installed copy when there is one, otherwise the first available to install.
 * @param {object} theme
 * @param {object} apps
 * @param {object} [installedApps]
 * @returns {object|undefined}
 */
export function getThemeStorefrontApp(theme, apps, installedApps) {
  const slugId = theme.theme?.storefront?.app;

  if (!slugId) {
    return undefined;
  }

  const storefrontApps = (apps?.results || []).filter(
    (app) => getAppSlugId(app) === slugId,
  );

  return (
    storefrontApps.find((app) =>
      installedApps?.results?.find(
        (installedApp) => installedApp.app_id === app.id,
      ),
    ) || storefrontApps[0]
  );
}

/**
 * The app ID to show to users: the public ID once the app is published,
 * otherwise the private ID with its internal `_` prefix removed.
 * @param {object} app
 * @returns {string}
 */
export function getAppDisplayId(app) {
  return app.public_id || stripPrivateIdPrefix(app.private_id);
}

/**
 * Render app price.
 * @param {object} app
 * @param {object} client
 * @returns {React.ReactNode}
 */
export function renderAppPrice(app, client) {
  const { price } = app;
  return (
    <>
      {isOwnApp(app, client)
        ? ''
        : price > 0
        ? formatCurrencyMaybeRound(price, 'USD')
        : 'Free'}
    </>
  );
}

/**
 * Get app developer URL.
 * @param {object} app
 * @param {object} owner
 * @return {string}
 */
export function getAppDeveloperUrl(app, owner) {
  return app.support_url || owner.support_website || app.repository_url;
}

/**
 * Get a human readable label for an app permission scope.
 * The API only ever grants access for `read_<resource>` and `write_<resource>`,
 * so anything else — `public` included — returns null rather than a label.
 * @param {string} permission
 * @returns {string|null}
 */
export function getAppPermissionLabel(permission) {
  const [action, ...rest] = permission.split('_');

  if (rest.length === 0) {
    return null;
  }

  const resource = wordify(rest.join('_')).toLowerCase();

  switch (action) {
    case 'read':
      return `View ${resource}`;
    case 'write':
      return `View and manage ${resource}`;
    default:
      return null;
  }
}

/**
 * Get the labelled access an app would be granted on install, leaving out
 * scopes that grant nothing and scopes that read the same as another (e.g.
 * `write_:files` and `write_files`). An app declaring no scopes skips the API
 * permission check altogether, which leaves it with unrestricted access.
 * @param {object} app
 * @returns {{permission: string, label: string}[]}
 */
export function getGrantedAppPermissions(app) {
  const permissions = app.permissions || [];

  if (permissions.length === 0) {
    return [{ permission: 'full_access', label: FULL_ACCESS_LABEL }];
  }

  return uniqBy(
    permissions
      .map((permission) => ({
        permission,
        label: getAppPermissionLabel(permission),
      }))
      .filter(({ label }) => label !== null),
    'label',
  );
}

/**
 * Describe what installing an app costs, or null when nothing is charged.
 * @param {object} app
 * @param {object} client
 * @returns {{description: string, amount?: string, period?: string}|null}
 */
export function getAppBilling(app, client) {
  if (isOwnApp(app, client)) {
    return null;
  }

  if (app.price_external) {
    return { description: EXTERNAL_PRICE_DESCRIPTION };
  }

  if (!(app.price > 0)) {
    return null;
  }

  // Apps predating price_interval bill monthly, so only an explicit 'once'
  // switches away from it — understating a recurring charge is the worse miss
  const isMonthly = app.price_interval !== 'once';

  return {
    description: isMonthly
      ? PRICE_DESCRIPTIONS.monthly
      : PRICE_DESCRIPTIONS.once,
    amount: formatCurrencyMaybeRound(app.price, 'USD'),
    period: isMonthly ? '/mo' : null,
  };
}

/**
 * Where a partner-facing app notification should go. `support_email` is
 * optional on a partner, so a notification addressed only at it silently fails
 * to send; the account owner always has an address because `users.email` is
 * required.
 *
 * @param {object} app — with `partner` and `partner.owner` expanded
 * @returns {string|undefined}
 */
export function getPartnerNotificationEmail(app) {
  return app.partner?.support_email || app.partner?.owner?.email;
}

/**
 * Whether an app config only holds the template body of a notification.
 * Each notification is pushed as a .json config plus a .tpl/.liquid config.
 *
 * @param {object} config
 * @returns {boolean}
 */
export function isNotificationTemplateConfig(config) {
  return (
    config.type === 'notification' &&
    /\.(tpl|liquid)$/.test(config.file_path || '')
  );
}

/**
 * Whether the user may run an app's settings actions (the app page Actions
 * menu and action fields in app settings). With roles this takes managing
 * integrations; otherwise it takes the settings permission that shows Apps.
 *
 * @param {object} user
 * @param {boolean} [isAdvancedUserPermissions]
 * @returns {boolean}
 */
export function canRunAppSettingsActions(user, isAdvancedUserPermissions) {
  if (isAdvancedUserPermissions) {
    return canManageRolePermission(user, 'integrations');
  }

  return hasPermission(user, 'settings');
}

/**
 * A role's access to apps. A role saved before it had the apps permission
 * has full access to apps only when it has full access to everything else.
 *
 * @param {object} [permissions] - role permissions
 * @returns {string} full, manage, view or restrict
 */
export function getRoleAppsPermission(permissions = {}) {
  if (permissions.apps) {
    return permissions.apps;
  }

  return Object.values(permissions).every((value) => value === 'full')
    ? 'full'
    : 'restrict';
}

/**
 * The user's access to app runs: with roles the apps permission (a user
 * without a role has full access); otherwise full permissions or the apps
 * permission.
 *
 * @param {object} user
 * @param {boolean} [isAdvancedUserPermissions]
 * @returns {string} full, manage, view or restrict
 */
function getRunsPermission(user, isAdvancedUserPermissions) {
  if (isAdvancedUserPermissions) {
    const permissions = user?.role?.permissions;
    return permissions ? getRoleAppsPermission(permissions) : 'full';
  }

  return hasPermission(user, 'apps') ? 'full' : 'restrict';
}

/**
 * Whether the user may manage installed apps, such as turning an app's AI on
 * or off: with roles an admin, or full or manage access to apps; otherwise
 * full permissions or the apps permission. The server checks the same
 *
 * @param {object} user
 * @param {boolean} [isAdvancedUserPermissions]
 * @returns {boolean}
 */
export function canManageApps(user, isAdvancedUserPermissions) {
  // `role === 'admin'` covers legacy admins
  if (
    isAdvancedUserPermissions &&
    (user?.role === 'admin' || user?.role?.type === 'admin')
  ) {
    return true;
  }

  return ['full', 'manage'].includes(
    getRunsPermission(user, isAdvancedUserPermissions),
  );
}

/**
 * Whether the user may see the runs of app workflows (the Workflows tab and its
 * runs). Users follow the runs they started regardless.
 *
 * @param {object} user
 * @param {boolean} [isAdvancedUserPermissions]
 * @returns {boolean}
 */
export function canSeeRuns(user, isAdvancedUserPermissions) {
  return ['full', 'manage', 'view'].includes(
    getRunsPermission(user, isAdvancedUserPermissions),
  );
}

/**
 * Whether the user may cancel runs of app workflows
 *
 * @param {object} user
 * @param {boolean} [isAdvancedUserPermissions]
 * @returns {boolean}
 */
export function canCancelRuns(user, isAdvancedUserPermissions) {
  return ['full', 'manage'].includes(
    getRunsPermission(user, isAdvancedUserPermissions),
  );
}

// Role permissions that guard writes to standard collections, as in the
// server's advanced permissions
const COLLECTION_ROLE_PERMISSIONS = {
  products: 'products',
  orders: 'orders',
  subscriptions: 'subscriptions',
  accounts: 'customers',
  coupons: 'coupons',
  promotions: 'promotions',
};

/**
 * Whether the user may run app actions on records of a collection (list,
 * bulk and record actions, and content action fields). With roles this takes
 * managing the collection; other collections and users are not restricted.
 *
 * @param {object} user
 * @param {boolean} [isAdvancedUserPermissions]
 * @param {string} [collection]
 * @returns {boolean}
 */
export function canRunAppContentActions(
  user,
  isAdvancedUserPermissions,
  collection,
) {
  const permission = COLLECTION_ROLE_PERMISSIONS[collection];

  return (
    !isAdvancedUserPermissions ||
    !permission ||
    canManageRolePermission(user, permission)
  );
}

/**
 * A user without a role has full access
 *
 * @param {object} user
 * @param {string} permission
 * @returns {boolean}
 */
function canManageRolePermission(user, permission) {
  const access = user?.role?.permissions?.[permission];
  return access !== 'restrict' && access !== 'view';
}

export const ENV_LIVE = 'live';
export const ENV_TEST = 'test';
export const ENV_ACTIVATE_TEST = 'activate_test';

const ENV_LABELS = { [ENV_LIVE]: 'Live', [ENV_TEST]: 'Test' };

/**
 * Environments an app can be installed to, when the store has a choice:
 * live and test, or live and a test environment yet to be activated. A store
 * with one environment only gets no choice. An environment the app is already
 * installed in stays listed, disabled, to say so.
 * @param {object} [account]
 * @param {Record<string, boolean>} [installedIn] by ENV_LIVE / ENV_TEST
 * @returns {{ value: string, label: string, disabled?: boolean }[]}
 */
export function getEnvironmentOptions(account, installedIn = {}) {
  if (!account?.hasLiveEnvironment) {
    return [];
  }

  const option = (value) =>
    installedIn[value]
      ? {
          value,
          label: `Already installed in ${ENV_LABELS[value]}`,
          disabled: true,
        }
      : { value, label: ENV_LABELS[value] };

  if (account.hasTestEnvironment) {
    return [option(ENV_LIVE), option(ENV_TEST)];
  }

  if (account.canHaveTestEnvironment) {
    return [
      option(ENV_LIVE),
      {
        value: ENV_ACTIVATE_TEST,
        label: 'Activate the test environment and install there',
      },
    ];
  }

  return [];
}
